import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import NewJobs from "./NewJobs";
import MyJobs from "./MyJobs";
import Spinner from "../common/Spinner";
import { connect } from "react-redux";
import * as jobActions from "../../redux/actions/jobActions";
import * as profileActions from "../../redux/actions/profileActions";
import { toast } from "react-toastify";
import { confirm } from "../common/ConfirmDialog";
import MyCompletedJobs from "./MyCompletedJob";
import CompleteJobPage from "./CompleteJobPage"; // eslint-disable-line import/no-named-as-default

const VIEW_NEW_JOBS = "new";
const VIEW_MY_JOBS = "myAssigned";
const VIEW_COMPLETED_JOBS = "viewCompleted";
const VIEW_JOB_COMPLETION = "viewJobCompletion";

export function JobsPage(props) {
  const {
    jobs,
    profile,
    claimedJob,
    loadJobs,
    loadProfile,
    claimJob,
    myJobs,
    loadMyJobs,
    myProgressedJobs,
    loadMyProgressedJobs,
    completeJob,
    completingJob,
    auth,
    apiCallsInProgress,
  } = props;
  const initialTab = new URLSearchParams(props.location?.search).get("tab");
  const [viewArea, setViewArea] = useState(initialTab || VIEW_NEW_JOBS);

  useEffect(() => {
    if (profile && Object.keys(profile).length === 0) {
      loadProfile(auth).catch((error) => {
        console.log("error loading profile");
        throw error;
      });
    }

    if (jobs.length === 0) {
      loadJobs(auth).catch((error) => {
        console.log("error loading jobs: " + error);
      });
    }

    if (profile && Object.keys(profile).length > 0 && myJobs.length === 0) {
      loadMyJobs(auth).catch((error) => {
        console.log("error loading my jobs: " + error);
      });
    }

    if (profile && Object.keys(profile).length > 0 && myProgressedJobs.length === 0) {
      loadMyProgressedJobs(auth).catch((error) => {
        console.log("error loading completed jobs: " + error);
      });
    }
  }, []);

  useEffect(() => {
    loadJobs(auth).catch((error) => {
      console.log("error loading jobs: " + error);
    });

    if (profile && Object.keys(profile).length > 0) {
      loadMyJobs(auth).catch((error) => {
        console.log("error loading my jobs: " + error);
      });
    }

    if (profile && Object.keys(profile).length > 0) {
      loadMyProgressedJobs(auth).catch((error) => {
        console.log("error loading completed jobs: " + error);
      });
    }
  }, [claimedJob, completingJob]);

  useEffect(() => {
    if (profile && Object.keys(profile).length > 0) {
      loadMyJobs(auth, profile).catch((error) => {
        console.log("error loading my jobs: " + error);
      });
    }

    if (profile && Object.keys(profile).length > 0 && myProgressedJobs.length === 0) {
      loadMyProgressedJobs(auth).catch((error) => {
        console.log("error loading completed jobs: " + error);
      });
    }
  }, [profile]);

  // Function to handle 'Claim Job' click event - show confirmation
  async function claimJobHandler(event) {
    const claim = jobs.find((e) => e.orderNumber === event.target.value);

    if (
      await confirm(`Are you sure you wish to assign job number ${claim.orderNumber}?`, "Yes", "Cancel", {
        title: `Confirm Assignment for ${profile.name.firstName} ${profile.name.lastName}`,
      })
    ) {
      // Implement code to make sure job is still available and then update with claimed engineer details
      claimJob(auth, claim, profile)
        .then(() => {
          toast.success(
            `Order ${claim.orderNumber} for ${claim.name.firstName} ${claim.name.lastName} has now been assigned to you`
          );
          Promise.all([loadJobs(auth), loadMyJobs(auth)]).then(() => setViewArea(VIEW_MY_JOBS));
        })
        .catch(() => {
          toast.error(
            `Failed to assign ${claim.orderNumber} for ${claim.name.firstName} ${claim.name.lastName} - please contact us`
          );
        });
    }
  }

  // Function to handle complete job
  async function completeJobHandler(event) {
    const jobToComplete = myJobs.find((e) => e.orderNumber === event.target.value);
    completeJob(jobToComplete);
    setViewArea(VIEW_JOB_COMPLETION);
  }

  function renderView() {
    switch (viewArea) {
      case VIEW_COMPLETED_JOBS:
        return <MyCompletedJobs myCompletedJobs={myProgressedJobs} />;

      case VIEW_MY_JOBS:
        return <MyJobs myJobs={myJobs} completeJobHandler={completeJobHandler} />;

      case VIEW_JOB_COMPLETION:
        return <CompleteJobPage auth={auth} profile={profile} completedJob={completingJob} />;

      default:
        return <NewJobs jobsToList={jobs} claimJobHandler={claimJobHandler} />;
    }
  }

  const isMyJobsTabActive = viewArea === VIEW_MY_JOBS || viewArea === VIEW_JOB_COMPLETION;

  return (
    <div className="container">
      <div className="row mb-3">
        <div className="col-md-12">
          <h2>Jobs</h2>
          <p className="text-muted">View details of new jobs, assign jobs and update your assigned jobs</p>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <ul className="nav nav-fill jobs-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link px-2 ${viewArea === VIEW_NEW_JOBS ? "active" : ""}`}
                onClick={() => setViewArea(VIEW_NEW_JOBS)}
              >
                New Jobs
                {jobs.length > 0 && <span className="badge bg-danger ms-2">{jobs.length}</span>}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link px-2 ${isMyJobsTabActive ? "active" : ""}`}
                onClick={() => setViewArea(VIEW_MY_JOBS)}
              >
                My Jobs
                {myJobs.length > 0 && <span className="badge bg-danger ms-2">{myJobs.length}</span>}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link px-2 ${viewArea === VIEW_COMPLETED_JOBS ? "active" : ""}`}
                onClick={() => setViewArea(VIEW_COMPLETED_JOBS)}
              >
                Completed
                {myProgressedJobs.length > 0 && <span className="badge bg-danger ms-2">{myProgressedJobs.length}</span>}
              </button>
            </li>
          </ul>
          <div className="p-0 p-md-3">
            {apiCallsInProgress > 0 && viewArea !== VIEW_JOB_COMPLETION ? <Spinner /> : renderView()}
          </div>
        </div>
      </div>
    </div>
  );
}

JobsPage.propTypes = {
  auth: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  loadProfile: PropTypes.func.isRequired,
  jobs: PropTypes.array.isRequired,
  loadJobs: PropTypes.func.isRequired,
  claimedJob: PropTypes.object.isRequired,
  claimJob: PropTypes.func.isRequired,
  myJobs: PropTypes.array.isRequired,
  myProgressedJobs: PropTypes.array.isRequired,
  loadMyProgressedJobs: PropTypes.func.isRequired,
  loadMyJobs: PropTypes.func.isRequired,
  apiCallsInProgress: PropTypes.number,
  completeJob: PropTypes.func,
  completingJob: PropTypes.object,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

function mapStateToProps(state) {
  return {
    profile: state.profile,
    jobs: state.jobs,
    claimedJob: state.claimedJob,
    myJobs: state.myJobs,
    myProgressedJobs: state.myProgressedJobs,
    apiCallsInProgress: state.apiCallsInProgress,
    completingJob: state.completingJob,
  };
}

const mapDispatchToProps = {
  loadJobs: jobActions.loadJobs,
  loadProfile: profileActions.loadProfile,
  claimJob: jobActions.claimJob,
  loadMyJobs: jobActions.loadMyJobs,
  loadMyProgressedJobs: jobActions.loadMyProgressedJobs,
  completeJob: jobActions.completeJob,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobsPage);
